/**
 * @fileoverview Opens the dialog for the My Page action the user chose.
 * @description Maps an action on a target to its dialog: Update Stage, Reopen, Reassign or Request
 * Reassignment, Send Payment Details and Confirm Payment. The page keeps the chosen action and the
 * target it was chosen on; this renders the matching dialog and nothing when none is chosen, so each
 * opening starts with a clean form.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import MyPageConfirmPaymentDialog from './MyPageConfirmPaymentDialog'
import MyPageReassignDialog from './MyPageReassignDialog'
import MyPageReopenDialog from './MyPageReopenDialog'
import MyPageSendPaymentDialog from './MyPageSendPaymentDialog'
import MyPageStageDialog from './MyPageStageDialog'

import type { MyPageActionRequest } from '@/features/sales/types/my-page.types'

interface MyPageActionHostProps {
  request: MyPageActionRequest | null
  onClose: () => void
}

/**
 * @component
 * @param {MyPageActionHostProps} props - The chosen action and the close handler.
 * @param {MyPageActionRequest | null} props.request - The action and its target, or null when none
 * is chosen.
 * @param {() => void} props.onClose - Called when a dialog is dismissed or its action is saved.
 */
function MyPageActionHost({ request, onClose }: MyPageActionHostProps) {
  if (!request) return null
  const { action, target } = request

  switch (action) {
    case 'UPDATE_STAGE':
      return <MyPageStageDialog target={target} onClose={onClose} />
    case 'REOPEN':
      return <MyPageReopenDialog target={target} onClose={onClose} />
    case 'REASSIGN':
      return <MyPageReassignDialog target={target} mode="reassign" onClose={onClose} />
    case 'REQUEST_REASSIGNMENT':
      return <MyPageReassignDialog target={target} mode="request" onClose={onClose} />
    case 'SEND_PAYMENT_DETAILS':
      return <MyPageSendPaymentDialog target={target} onClose={onClose} />
    case 'CONFIRM_PAYMENT':
      return <MyPageConfirmPaymentDialog target={target} onClose={onClose} />
  }
}

export default MyPageActionHost
